import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  academicApi,
  CreateProgramRequest,
  OpenTermRequest,
  CreateCourseRequest,
  CreateSectionRequest,
  UpdateProgramRequest,
  DeleteProgramRequest,
  UpdateTermRequest,
  DeleteTermRequest,
  HardDeleteTermRequest,
  UpdateCourseRequest,
  DeleteCourseRequest,
  UpdateSectionRequest,
  DeleteSectionRequest,
  HardDeleteSectionRequest,
  SetSectionLifecycleRequest,
} from '../lib/api/academic.api';

export function useAcademic() {
  const queryClient = useQueryClient();

  const createProgramMutation = useMutation({
    mutationFn: (data: CreateProgramRequest) => academicApi.createProgram(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['programs'] }),
  });

  const createTermMutation = useMutation({
    mutationFn: (data: OpenTermRequest) => academicApi.createTerm(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['terms'] }),
  });

  const createCourseMutation = useMutation({
    mutationFn: (data: CreateCourseRequest) => academicApi.createCourse(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['courses'] }),
  });

  const createSectionMutation = useMutation({
    mutationFn: (data: CreateSectionRequest) => academicApi.createSection(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sections'] }),
  });

  const updateProgramMutation = useMutation({
    mutationFn: (data: UpdateProgramRequest) => academicApi.updateProgram(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['programs'] }),
  });

  const deleteProgramMutation = useMutation({
    mutationFn: (data: DeleteProgramRequest) => academicApi.deleteProgram(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['programs'] }),
  });

  const updateTermMutation = useMutation({
    mutationFn: (data: UpdateTermRequest) => academicApi.updateTerm(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['terms'] }),
  });

  const deleteTermMutation = useMutation({
    mutationFn: (data: DeleteTermRequest) => academicApi.deleteTerm(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['terms'] }),
  });

  const hardDeleteTermMutation = useMutation({
    mutationFn: (data: HardDeleteTermRequest) => academicApi.hardDeleteTerm(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['terms'] }),
  });

  const updateCourseMutation = useMutation({
    mutationFn: (data: UpdateCourseRequest) => academicApi.updateCourse(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['courses'] }),
  });

  const deleteCourseMutation = useMutation({
    mutationFn: (data: DeleteCourseRequest) => academicApi.deleteCourse(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['courses'] }),
  });

  const updateSectionMutation = useMutation({
    mutationFn: (data: UpdateSectionRequest) => academicApi.updateSection(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sections'] }),
  });

  const deleteSectionMutation = useMutation({
    mutationFn: (data: DeleteSectionRequest) => academicApi.deleteSection(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sections'] }),
  });

  const hardDeleteSectionMutation = useMutation({
    mutationFn: (data: HardDeleteSectionRequest) => academicApi.hardDeleteSection(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sections'] }),
  });

  const setSectionLifecycleMutation = useMutation({
    mutationFn: (data: SetSectionLifecycleRequest) => academicApi.setSectionLifecycle(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sections'] }),
  });

  return {
    createProgram: createProgramMutation.mutateAsync,
    isCreatingProgram: createProgramMutation.isPending,
    createTerm: createTermMutation.mutateAsync,
    isCreatingTerm: createTermMutation.isPending,
    createCourse: createCourseMutation.mutateAsync,
    isCreatingCourse: createCourseMutation.isPending,
    createSection: createSectionMutation.mutateAsync,
    isCreatingSection: createSectionMutation.isPending,
    updateProgram: updateProgramMutation.mutateAsync,
    isUpdatingProgram: updateProgramMutation.isPending,
    deleteProgram: deleteProgramMutation.mutateAsync,
    isDeletingProgram: deleteProgramMutation.isPending,
    updateTerm: updateTermMutation.mutateAsync,
    isUpdatingTerm: updateTermMutation.isPending,
    deleteTerm: deleteTermMutation.mutateAsync,
    isDeletingTerm: deleteTermMutation.isPending,
    hardDeleteTerm: hardDeleteTermMutation.mutateAsync,
    isHardDeletingTerm: hardDeleteTermMutation.isPending,
    updateCourse: updateCourseMutation.mutateAsync,
    isUpdatingCourse: updateCourseMutation.isPending,
    deleteCourse: deleteCourseMutation.mutateAsync,
    isDeletingCourse: deleteCourseMutation.isPending,
    updateSection: updateSectionMutation.mutateAsync,
    isUpdatingSection: updateSectionMutation.isPending,
    deleteSection: deleteSectionMutation.mutateAsync,
    isDeletingSection: deleteSectionMutation.isPending,
    hardDeleteSection: hardDeleteSectionMutation.mutateAsync,
    isHardDeletingSection: hardDeleteSectionMutation.isPending,
    setSectionLifecycle: setSectionLifecycleMutation.mutateAsync,
    isSettingSectionLifecycle: setSectionLifecycleMutation.isPending,
  };
}
